/**
 * Preparing evidence on the client, before anything touches the network.
 *
 * Two jobs, both of them decided in ADR 0005:
 *
 *  1. Downscale. A phone photo is 2-4 MB; at 1600px/q80 it is a few hundred KB.
 *     That is the difference between ~2.5 TB/year and ~350 GB/year, and it is
 *     also what makes a 4 MB upload not be the thing that fails in the field.
 *     The numbers are a judgement from typical device output, not a measurement.
 *
 *  2. Drop the EXIF. A photo of a signed lease carries the GPS fix of where the
 *     owner signed, the device serial, and a timestamp. We are already deleting
 *     evidence by statute, so holding that is indefensible. Re-encoding through
 *     a canvas strips it for free, because canvas pixels carry no metadata --
 *     but only if we re-encode, which is also why an undecodable file is passed
 *     through untouched rather than mangled.
 */

import { sha256Hex } from "./sha256";

const LONG_EDGE = 1600;
const START_QUALITY = 0.8;
const MIN_QUALITY = 0.5;
const MAX_IMAGE_BYTES = 1024 * 1024;
const MAX_DOCUMENT_BYTES = 10 * 1024 * 1024;

export type Prepared = {
  blob: Blob;
  sha256: string;
  kind: "photo" | "document";
  /** Set when we could not re-encode, so the UI can say why the file is large. */
  note: string | null;
  width: number | null;
  height: number | null;
};

export { sha256Hex };

type Drawable = { src: CanvasImageSource; w: number; h: number };

/** Decodes via createImageBitmap, falling back to an <img> for Safari oddities. */
async function rasterize(file: File): Promise<Drawable | null> {
  try {
    // 'from-image' applies the EXIF orientation, so a phone photo taken sideways
    // comes out the right way up rather than needing a second fix.
    const bmp = await createImageBitmap(file, { imageOrientation: "from-image" });
    return { src: bmp, w: bmp.width, h: bmp.height };
  } catch {
    /* fall through */
  }
  try {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    await img.decode();
    const d = { src: img, w: img.naturalWidth, h: img.naturalHeight };
    // The object URL stays alive for the life of the drawable; the page going
    // away is what releases it, and the canvas has already been painted by then.
    return d;
  } catch {
    return null;
  }
}

function canvasToBlob(c: HTMLCanvasElement, quality: number): Promise<Blob | null> {
  return new Promise((resolve) => c.toBlob(resolve, "image/jpeg", quality));
}

export async function prepareEvidence(file: File): Promise<Prepared> {
  const isImage = file.type.startsWith("image/");

  if (!isImage) {
    if (file.size > MAX_DOCUMENT_BYTES) {
      throw new Error(`Berkas ${(file.size / 1e6).toFixed(1)} MB melebihi batas 10 MB.`);
    }
    return { blob: file, sha256: await sha256Hex(file), kind: "document", note: null, width: null, height: null };
  }

  const d = await rasterize(file);
  if (!d || d.w === 0 || d.h === 0) {
    // HEIC the browser cannot decode. The ADR's rule: store what we were given,
    // with the honest content type, and lose nothing. We never claim to have
    // stripped metadata from bytes we could not read.
    return {
      blob: file,
      sha256: await sha256Hex(file),
      kind: "photo",
      note: "Format ini tidak dapat dikecilkan di perangkat. Berkas dikirim apa adanya.",
      width: null,
      height: null,
    };
  }

  const scale = Math.min(1, LONG_EDGE / Math.max(d.w, d.h));
  const tw = Math.max(1, Math.round(d.w * scale));
  const th = Math.max(1, Math.round(d.h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Perangkat tidak dapat memproses gambar.");
  ctx.drawImage(d.src, 0, 0, tw, th);
  if ("close" in d.src && typeof d.src.close === "function") d.src.close();

  let quality = START_QUALITY;
  let out = await canvasToBlob(canvas, quality);
  // Trade resolution for bytes only if we must: a signature has to stay legible,
  // so quality goes first, and only a second pass shrinks the canvas.
  while (out && out.size > MAX_IMAGE_BYTES && quality > MIN_QUALITY) {
    quality = Math.max(MIN_QUALITY, quality - 0.1);
    out = await canvasToBlob(canvas, quality);
  }
  while (out && out.size > MAX_IMAGE_BYTES && tw > 900 && th > 900) {
    const shrink = 0.8;
    canvas.width = Math.round(canvas.width * shrink);
    canvas.height = Math.round(canvas.height * shrink);
    const c2 = canvas.getContext("2d");
    if (!c2) break;
    c2.drawImage(d.src, 0, 0, canvas.width, canvas.height);
    out = await canvasToBlob(canvas, quality);
  }
  if (!out) throw new Error("Gagal memproses gambar.");
  if (out.size > MAX_IMAGE_BYTES) {
    throw new Error(`Foto masih ${(out.size / 1e6).toFixed(1)} MB setelah dikecilkan.`);
  }

  return {
    blob: out,
    sha256: await sha256Hex(out),
    kind: "photo",
    note: null,
    width: tw,
    height: th,
  };
}
